<script setup lang="ts">
import { Card } from '@/components/ui/card'
import PageHeader from '@/components/PageHeader.vue'
import { onMounted, ref } from 'vue'
import { toast } from 'vue-sonner'
import aplosApi from '@/services/aplosApi'
import { type TenantL1Object, type User } from '@/types'
import moment from 'moment'
import { getLocalizedDateTimeString, toShortString, toTitleCase } from '@/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { useUser } from '@/composables/useUser'
import Loading from '@/components/Loading.vue'
import { searchColorMap } from '@/utils/index'
import AppMenuBar from "@/components/Menu/AppMenuBar.vue";
import {  RefreshCw } from "lucide-vue-next";
const auth = useUser()



// Props
type Props = {
  displayLimit?: number
  search?: string
}
const props = defineProps<Props>()

// State
const loading = ref(true)
const originalTenants = ref<TenantL1Object[]>([]);
const tenants = ref<TenantL1Object[]>([])
const lastRefresh = ref("")

// Table Columns
const columns = ref([
  { title: 'Tenant ID', dataIndex: 'tenant.id', key: 'tenant.id' },
  { title: 'Tenant Name', dataIndex: 'tenant.name', key: 'tenant.name', sorter: (a: TenantL1Object, b: TenantL1Object) => a.tenant.name.localeCompare(b.tenant.name) },
  { title: 'Tenant Status', dataIndex: 'tenant.status', key: 'tenant.status' },
  { title: 'Subscription Status', dataIndex: 'tenant.subscription.status', key: 'tenant.subscription.status' },
  {
    title: 'Executions (Completed / Failed)',
    key: 'executions',
    customRender: ( record: TenantL1Object ) => `${record.tenant?.metrics?.completed || 0} / ${record.tenant?.metrics?.failed || 0}`
  },
  { title: 'Subscription End Date', dataIndex: 'tenant.subscription.end_utc', key: 'tenant.subscription.end_utc', sorter: (a: TenantL1Object, b: TenantL1Object) => moment(a.tenant.subscription.end_utc).diff(moment(b.tenant.subscription.end_utc)) }
])


function is_global_admin() {

    return auth.user.roles?.some(item => item.includes("aplos_nca:global:developer")
      || item.includes("aplos_nca:global:admin"))

  }

// Load Data
async function loadData() {
  loading.value = true
  try {
    const global_admin = is_global_admin()
    const tenant_id =  global_admin ? "*" : auth.user.tenant_id;
    const { data } = await aplosApi.getTenantMetrics(tenant_id)
    const t = Array.isArray(data.tenants) ? data.tenants : [data];
    originalTenants.value = t;
    tenants.value = t
    lastRefresh.value = data.updated?.utc
    lastRefresh.value = `Report Last Updated on: ${getLocalizedDateTimeString(data.updated?.utc)}`;
  } catch (error) {
    console.error(error)
    toast.error('Failed to load Tenants')
  } finally {
    loading.value = false
  }
}

function handleSearch(event: Event) {
  try{
    let searchKeyword = (event.target as HTMLInputElement).value.toLowerCase();
    loading.value = true
    if (searchKeyword.length > 0) {
      const filterRecords = originalTenants.value.filter(data => {
        let tenant = data.tenant;
        let tenantStatus = '';
        let name = tenant.name.toLowerCase();
        if (tenant.status) {
          tenantStatus = tenant.status.toLowerCase();
        }
        let subsType = tenant.subscription_type?.toLowerCase();
        let subsStatus = tenant.subscription_status?.toLowerCase();
        let subsMaxUsers = '';
        let start_date = ''
        let end_date = ''
        let tenant_id = ''

        tenant_id = tenant.id.toLocaleLowerCase()

        if (tenant.start_utc) {
          let localized = getLocalizedDateTimeString(tenant.start_utc).toLocaleLowerCase()
          start_date = localized
        }
        if (tenant.end_utc) {
          let localized = getLocalizedDateTimeString(tenant.end_utc).toLocaleLowerCase()
          end_date = localized
        }

        const fieldsToCheck = [name, tenantStatus, subsType,
          subsStatus, subsMaxUsers, start_date, end_date, tenant_id]
        return fieldsToCheck.some(field => field?.includes(searchKeyword))

      });
      tenants.value = props.displayLimit ? filterRecords.slice(0, props.displayLimit) : filterRecords;
    } else {
      tenants.value = props.displayLimit ? originalTenants.value.slice(0, props.displayLimit) : originalTenants.value;
    }
  } catch(err) {
    console.warn(err)
  } finally {

    loading.value = false
  }


}



// Lifecycle
onMounted(async() => {
  await auth.initialize()
  await loadData();

})


const userColumns = [
  { title: 'First Name', dataIndex: 'first_name', key: 'first_name' },
  { title: 'Last Name', dataIndex: 'last_name', key: 'last_name' },
  { title: 'Email', dataIndex: 'email_address', key: 'email_address' },

  {
    title: 'Executions (Completed / Failed)',
    key: 'executions',
    customRender: ( { record }: { record: User } ) => `${record.metrics?.completed || 0} / ${record.metrics?.failed || 0}`
  },

];

const navButtons = ref([
  {
    icon: RefreshCw,
    onClick: loadData,
    animate: loading,
    title: "",
    toolTip: "Refresh",
  },
  // Add more button objects as needed...
]);
</script>

<template>
  <PageHeader title="Administration - View User & Tenant Metrics" />

  <Card class="p-6 rounded-3xl dark:bg-[#0d1526]">
    <AppMenuBar
      :showSearch="true"
      :onSearch="handleSearch"
      :messages="[lastRefresh]"
      :buttons="navButtons"
    />
    <Loading v-if="loading"></Loading>

    <div v-else>
      <div class="overflow-x-auto">
        <a-table
          :dataSource="tenants"
          :columns="columns"
          :pagination="
            props.displayLimit ? false : { showSizeChanger: tenants.length >= 20 }
          "
          :rowKey="(record: TenantL1Object) => record.tenant.id"
        >
          <!-- Custom Render Slot for Table Cells -->
          <template #bodyCell="{ column, record }">
            <!-- Loading State -->
            <template v-if="loading">
              <Skeleton class="my-4 h-6 w-full" />
            </template>

            <!-- Tenant ID (Shortened with Tooltip) -->
            <template v-else-if="column.key === 'tenant.id'">
              <a-tooltip>
                <template #title>{{ record.tenant.id }}</template>
                {{ toShortString(record.tenant.id, 10) }}
              </a-tooltip>
            </template>

            <!-- Tenant Name -->
            <template v-else-if="column.key === 'tenant.name'">
              {{ record.tenant.name }}
            </template>

            <!-- Tenant Status -->
            <template v-else-if="column.key === 'tenant.status'">
              <Badge :class="searchColorMap(record.tenant.status)">
                {{ toTitleCase(record.tenant.status) }}
              </Badge>
            </template>

            <template v-else-if="column.key === 'tenant.subscription.status'">
              <Badge :class="searchColorMap(record.tenant.subscription.status)">
                {{ toTitleCase(record.tenant.subscription.status) }}
              </Badge>
            </template>

            <!-- Executions (Completed / Failed) -->
            <template v-else-if="column.key === 'executions'">
              <div
                v-if="!record.tenant.metrics.completed && !record.tenant.metrics.failed"
              >
                ------
              </div>

              <div v-else class="flex items-center gap-3">
                <Badge v-if="record.tenant.metrics.completed" class="bg-brand-green">
                  {{ record.tenant.metrics.completed }}
                </Badge>

                <Badge v-if="record.tenant.metrics.failed" class="bg-[#ff5757]">
                  {{ record.tenant.metrics.failed }}
                </Badge>
              </div>
            </template>

            <!-- Subscription End Date (Formatted) -->
            <template v-else-if="column.key === 'tenant.subscription.end_utc'">
              {{ getLocalizedDateTimeString(record.tenant.subscription.end_utc) }}
            </template>
          </template>

          <!-- sub records -->
          <template #expandedRowRender="{ record }">
            <div class="nested-table-wrapper">
              <a-table
                :dataSource="record.users"
                :columns="userColumns"
                :pagination="false"
                :showHeader="true"
              >
                <template #bodyCell="{ column, record }">
                  <template v-if="column.key === 'executions'">
                    <div v-if="!record.metrics.completed && !record.metrics.failed">
                      ------
                    </div>

                    <div v-else class="flex items-center gap-3">
                      <Badge v-if="record.metrics.completed" class="bg-brand-green">
                        {{ record.metrics.completed }}
                      </Badge>

                      <Badge v-if="record.metrics.failed" class="bg-[#ff5757]">
                        {{ record.metrics.failed }}
                      </Badge>
                    </div>
                  </template>
                </template>
              </a-table>
            </div>
          </template>
        </a-table>
      </div>
    </div>
  </Card>
</template>
